import { useEffect, useRef } from "react";
import { ArrowLeft, ExternalLink, Check, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BOOK, openAmazon, bmTrack, BM_EVENTS } from "@/lib/businessMastery";
import { BusinessMasteryCover } from "@/components/BusinessMasteryCard";

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-heading text-xl text-white mb-3">{title}</h2>
      {children}
    </div>
  );
}

export default function BusinessMastery() {
  const navigate = useNavigate();
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    bmTrack(BM_EVENTS.view, { source: "business_mastery_page", location: "dedicated_page" });
  }, []);
  const buy = (location) => openAmazon({ source: "business_mastery_page", location });

  return (
    <div className="fv-fade-up max-w-4xl" data-testid="business-mastery-page">
      <button onClick={() => navigate("/education")} className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-5">
        <ArrowLeft className="w-4 h-4" /> Back to Academy
      </button>

      <div className="flex flex-col sm:flex-row gap-6 mb-8">
        <BusinessMasteryCover className="w-32 h-44 sm:w-36 sm:h-52" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Featured educational resource</div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white mb-1">{BOOK.title}</h1>
          <div className="text-[#A1A1AA] mb-1">By {BOOK.author}</div>
          <div className="text-sm text-[#71717A] mb-4">Available on Amazon</div>
          <Button onClick={() => buy("hero")} data-testid="bm-buy-hero"
            className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-11 px-6 hover:opacity-90">
            Purchase on Amazon <ExternalLink className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>

      <Section title="About the Book">
        <p className="text-[#A1A1AA] leading-relaxed">{BOOK.description}</p>
      </Section>

      <Section title="What You'll Learn">
        <ul className="space-y-2">
          {BOOK.learn.map((l) => (
            <li key={l} className="flex items-start gap-2.5 text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />{l}</li>
          ))}
        </ul>
      </Section>

      <Section title="Who It's For">
        <ul className="space-y-2">
          {BOOK.who.map((w) => (
            <li key={w} className="flex items-start gap-2.5 text-[#D4D4D8]"><Check className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />{w}</li>
          ))}
        </ul>
      </Section>

      <Section title="How It Complements Fairview Systems">
        <p className="text-[#A1A1AA] leading-relaxed">
          Fairview Systems gives you the operating system to build and manage your business. Business Mastery
          deepens the thinking behind it — read a chapter, then apply it in your Fairview workflows, courses, and
          tools. The book's end-of-chapter exercises pair naturally with your day-to-day work inside the platform.
        </p>
      </Section>

      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6" data-testid="bm-purchase-section">
        <div className="flex items-center gap-2 mb-2"><BookOpen className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-heading text-xl text-white">Purchase the Book</h2></div>
        <p className="text-sm text-[#A1A1AA] mb-4">Business Mastery is available for purchase on Amazon.</p>
        <Button onClick={() => buy("purchase_section")} data-testid="bm-buy-bottom"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-11 px-6 hover:opacity-90">
          Purchase on Amazon <ExternalLink className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
